import type { JSONDocNode } from '@atlaskit/editor-json-transformer';

/**
 * The result of `analyzeOutcome` — a structural-delta summary of what changed
 * between the pre-session and post-session ADF documents, classified by whether
 * the change happened inside or outside the model's intended target region.
 *
 * Consumed by the `success_but_suspicious` outcome-class wiring (CCI-15893)
 * and by downstream dashboards.
 *
 * Notes on `unintendedRegion*` semantics: "intended" here means "explicitly
 * named as a target by one of the tool calls executed during the session"
 * (i.e. present in `affectedLocalIds`). When the backend ships the
 * `selectionLocalIds` field as part of the streaming payload, the analyzer
 * will be upgraded (CCI-15978) to use that as the canonical source of intent
 * and fall back to `affectedLocalIds` only when the backend-supplied field is absent.
 */
export interface OutcomeAnalysis {
	/** All localIds that were explicit tool-call targets during the session. */
	affectedLocalIds: Set<string>;
	/**
	 * The session's inferred intent (derived from the mix of tool-call types
	 * actually applied) does not match the structural delta. Examples:
	 * - APPEND intent (only insert / append commands) but `totalNodeDelta < 0`.
	 * - REWRITE intent (predominantly `replaceNode` / `setAttrs`) but
	 *   `|totalNodeDelta| ≥ 2` (the apply layer added or dropped nodes the
	 *   model didn't ask to touch).
	 * - REMOVE intent (only `removeNode` / `removeNodes`) but
	 *   `totalNodeDelta ≥ 0` (the apply layer didn't actually remove anything).
	 *
	 * Conservative — sessions with mixed or unclassifiable intent never fire
	 * this flag because the heuristic would be too noisy. The signal is
	 * deliberately marked `experimental: true` on the wire because intent is
	 * inferred (not yet supplied by the backend).
	 */
	intentEffectMismatch: boolean;
	/**
	 * The session removed top-level content whose `localId` was outside the
	 * user's selection. Always `false` until the backend ships
	 * `selectionLocalIds` and the FE plumbs it through to this analyzer; see
	 * the follow-up ticket for the heuristic implementation.
	 */
	protectedContentRemoved: boolean;
	/** Top-level nodes present before but absent after. */
	removedNodeCount: number;
	/** Total text content length after minus before. */
	textCharacterDelta: number;
	/** Top-level node count after minus before. Negative => net removal. */
	totalNodeDelta: number;
	/** Top-level node still present after but with text content changed, that
	 * was NOT an explicit target. Only detects modifications to nodes with
	 * a `localId` (the only way to identify the same node across pre/post
	 * snapshots without a deep diff); changes to unlabelled top-level
	 * nodes are not surfaced by this flag.
	 *
	 * A `±1` character tolerance is applied per node to absorb ProseMirror
	 * normalisation artefacts (single trailing whitespace, newline,
	 * punctuation injection/strip on block close), matching the same
	 * tolerance used at the session-wide `noMeaningfulEffect` derivation
	 * site. Per-node deltas of `±1` do not count as "modified".
	 *
	 * Known limitation: text changes are detected by comparing total
	 * character length pre vs post. Same-length rewrites (e.g. "hello" →
	 * "world" in a non-target paragraph) are silent false negatives. A
	 * deeper diff (token / hash) would catch these but is out of scope
	 * for v1; revisit once dashboards exist to measure how often the gap
	 * matters. */
	unintendedRegionModified: boolean;
	/** Top-level node disappeared between before and after, that was NOT an
	 * explicit target (i.e. nothing in the tool-call list told us this node
	 * was supposed to disappear). */
	unintendedRegionRemoved: boolean;
}

/**
 * A condensed record of one tool call executed during the session. Used to
 * collect `affectedLocalIds` so the analyzer can distinguish intentional
 * removals/modifications from collateral damage.
 */
export interface ToolCallRecord {
	commandType: string;
	targetLocalId?: string;
	targetLocalIds?: string[];
}

/**
 * Top-level ADF / PM JSON document shape. We only need `content` here.
 * Exported so the caller has a one-name cast target for the `toJSON()`
 * outputs of `prosemirror-model` Nodes (whose return type is too loose
 * to satisfy this shape directly).
 */
export type DocLike = JSONDocNode | { content?: unknown[]; type: string };

interface TopLevelNodeRecord {
	localId: string | undefined;
	textLength: number;
}

/**
 * Walk the top-level children of `doc` and collect their localId + text-length
 * fingerprint. The fingerprint is used to detect "node still present but its
 * text changed" without paying for a full deep diff.
 */
function snapshotTopLevel(doc: DocLike): {
	byLocalId: Map<string, TopLevelNodeRecord>;
	totalNodes: number;
	totalTextLength: number;
} {
	const byLocalId = new Map<string, TopLevelNodeRecord>();
	let totalNodes = 0;
	let totalTextLength = 0;

	const content = (doc as { content?: unknown[] }).content;
	if (!Array.isArray(content)) {
		return { byLocalId, totalNodes, totalTextLength };
	}

	for (const child of content) {
		if (child === null || typeof child !== 'object') {
			continue;
		}
		totalNodes += 1;
		const node = child as { attrs?: { localId?: unknown }; content?: unknown[] };
		const localId =
			typeof node.attrs?.localId === 'string' ? node.attrs.localId : undefined;
		const textLength = collectTextLength(node);
		totalTextLength += textLength;
		if (localId !== undefined) {
			byLocalId.set(localId, { localId, textLength });
		}
	}

	return { byLocalId, totalNodes, totalTextLength };
}

/**
 * Sum up the length of every text node in the subtree rooted at `node`.
 * Robust to malformed input — anything that doesn't look like a node is
 * skipped.
 */
function collectTextLength(node: unknown): number {
	if (node === null || typeof node !== 'object') {
		return 0;
	}
	const n = node as { content?: unknown[]; text?: unknown; type?: unknown; };
	let total = 0;
	if (n.type === 'text' && typeof n.text === 'string') {
		total += n.text.length;
	}
	if (Array.isArray(n.content)) {
		for (const child of n.content) {
			total += collectTextLength(child);
		}
	}
	return total;
}

/**
 * Iterate over a list of tool-call records and collect every localId that
 * was named as an explicit target.
 */
export function collectAffectedLocalIds(toolCalls: ToolCallRecord[]): Set<string> {
	const ids = new Set<string>();
	for (const record of toolCalls) {
		if (record.targetLocalId !== undefined) {
			ids.add(record.targetLocalId);
		}
		if (Array.isArray(record.targetLocalIds)) {
			for (const id of record.targetLocalIds) {
				ids.add(id);
			}
		}
	}
	return ids;
}

export interface AnalyzeOutcomeArgs {
	postSessionAdf: DocLike;
	preSessionAdf: DocLike;
	/**
	 * From the backend streaming payload. Optional today (the FE upgrade to consume
	 * this is tracked by CCI-15978). When supplied, will be merged with
	 * `affectedLocalIds` so the "intended region" includes both the
	 * backend-declared selection and what the tool calls actually touched.
	 */
	selectionLocalIds?: string[];
	toolCalls: ToolCallRecord[];
}

/**
 * Compute a structural-delta summary of what happened between
 * `preSessionAdf` and `postSessionAdf`, classified by whether the change
 * happened inside or outside the model's intended target region.
 *
 * The analyzer is intentionally cheap — it walks top-level children only and
 * does not do a deep tree diff. That keeps it suitable for end-of-session
 * invocation without slowing down user-perceived latency, while still
 * surfacing the most consequential anomalies (top-level node removed, top-
 * level node text content significantly changed).
 *
 * `protectedContentRemoved` is currently always `false`; the heuristic that
 * fills it in depends on backend-supplied `selectionLocalIds` which is not
 * yet plumbed end-to-end. See the follow-up ticket for the implementation.
 */

/**
 * Categories of session intent inferred from the mix of tool-call types
 * actually applied during the session.
 *
 * - `'append'`   — every applied call is purely additive (`appendNode`,
 *                  `appendNodeToEnd`, `insertNodeAfter`, `insertNodeBefore`).
 *                  Net node delta is expected to be `≥ 0`.
 * - `'rewrite'`  — every applied call is in-place (`replaceNode`, `setAttrs`,
 *                  `wrap`, `wrapNode`). Net node delta is expected to be
 *                  small (`|delta| ≤ 1` accounting for ProseMirror
 *                  normalisation).
 * - `'remove'`   — every applied call is destructive (`removeNode`,
 *                  `removeNodes`, `moveNode`). Net node delta is expected
 *                  to be `< 0`.
 * - `'mixed'`    — multiple intent categories present, OR no recognised
 *                  command types ran. The intent-effect check is skipped
 *                  for this category because the heuristic would be too
 *                  noisy to be useful.
 */
type SessionIntent = 'append' | 'rewrite' | 'remove' | 'mixed';

const APPEND_INTENT_COMMANDS: ReadonlySet<string> = new Set([
	'append',
	'appendNode',
	'appendNodeToEnd',
	'insertNodeAfter',
	'insertNodeBefore',
]);

const REWRITE_INTENT_COMMANDS: ReadonlySet<string> = new Set([
	'replaceNode',
	'setAttrs',
	'wrap',
	'wrapNode',
]);

const REMOVE_INTENT_COMMANDS: ReadonlySet<string> = new Set([
	'removeNode',
	'removeNodes',
	'moveNode',
]);

/**
 * Infer the session's intent from the mix of tool-call types actually applied.
 *
 * Conservative — returns `'mixed'` whenever the call list spans more than
 * one intent category, or when no recognised command types ran. The caller
 * should treat `'mixed'` as "skip the intent-effect check".
 */
function inferSessionIntent(toolCalls: readonly ToolCallRecord[]): SessionIntent {
	let hasAppend = false;
	let hasRewrite = false;
	let hasRemove = false;
	for (const call of toolCalls) {
		if (APPEND_INTENT_COMMANDS.has(call.commandType)) {
			hasAppend = true;
		} else if (REWRITE_INTENT_COMMANDS.has(call.commandType)) {
			hasRewrite = true;
		} else if (REMOVE_INTENT_COMMANDS.has(call.commandType)) {
			hasRemove = true;
		}
	}
	const categoryCount = (hasAppend ? 1 : 0) + (hasRewrite ? 1 : 0) + (hasRemove ? 1 : 0);
	if (categoryCount !== 1) {
		// Either zero recognised categories (no signal) or two-or-more
		// (genuinely mixed — heuristic too noisy to apply).
		return 'mixed';
	}
	if (hasAppend) {
		return 'append';
	}
	if (hasRewrite) {
		return 'rewrite';
	}
	return 'remove';
}

/**
 * Decide whether the structural delta contradicts the inferred intent.
 *
 * Threshold rationale:
 * - `'append'`  — any net removal (`totalNodeDelta < 0`) is a mismatch.
 *                  Net delta of `0` is allowed: a `replaceNode`-shaped
 *                  command can produce zero delta even with append intent
 *                  (this case is rare but not a contradiction).
 * - `'rewrite'` — `|totalNodeDelta| ≥ 2` is a mismatch. The `±1` window
 *                  matches the same ProseMirror normalisation tolerance
 *                  applied at the `noMeaningfulEffect` derivation site
 *                  (single trailing whitespace / newline can shift the
 *                  count by 1 without indicating a real structural change).
 * - `'remove'`  — `totalNodeDelta ≥ 0` is a mismatch (the apply layer
 *                  didn't actually remove anything despite remove intent).
 */
function detectIntentEffectMismatch(
	intent: SessionIntent,
	totalNodeDelta: number,
): boolean {
	switch (intent) {
		case 'append':
			return totalNodeDelta < 0;
		case 'rewrite':
			return Math.abs(totalNodeDelta) >= 2;
		case 'remove':
			return totalNodeDelta >= 0;
		case 'mixed':
		default:
			return false;
	}
}

export function analyzeOutcome({
	preSessionAdf,
	postSessionAdf,
	toolCalls,
	selectionLocalIds,
}: AnalyzeOutcomeArgs): OutcomeAnalysis {
	const pre = snapshotTopLevel(preSessionAdf);
	const post = snapshotTopLevel(postSessionAdf);

	const affectedLocalIds = collectAffectedLocalIds(toolCalls);
	if (Array.isArray(selectionLocalIds)) {
		for (const id of selectionLocalIds) {
			affectedLocalIds.add(id);
		}
	}

	let removedNodeCount = 0;
	let unintendedRegionRemoved = false;
	for (const [localId] of pre.byLocalId) {
		if (!post.byLocalId.has(localId)) {
			removedNodeCount += 1;
			if (!affectedLocalIds.has(localId)) {
				unintendedRegionRemoved = true;
			}
		}
	}

	// Account for unlabelled top-level nodes (no `localId` attr).
	// These can't be targeted by any tool call (every command keys off
	// `localId`), so a net drop in unlabelled-node count is by definition
	// unintended collateral damage. Tracking it here keeps
	// `removedNodeCount` consistent with `totalNodeDelta` and surfaces
	// the most clearly-anomalous removals on the `unintendedRegionRemoved`
	// flag.
	//
	// Known limitation: an equal-count swap (one unlabelled node removed
	// AND one unlabelled node added in the same session) is invisible to
	// this counter. Detecting it would require position/content matching,
	// which the analyzer intentionally avoids for cost reasons. Net delta
	// is sufficient for current dashboard needs.
	const preUnlabelledCount = pre.totalNodes - pre.byLocalId.size;
	const postUnlabelledCount = post.totalNodes - post.byLocalId.size;
	if (postUnlabelledCount < preUnlabelledCount) {
		const unlabelledRemoved = preUnlabelledCount - postUnlabelledCount;
		removedNodeCount += unlabelledRemoved;
		unintendedRegionRemoved = true;
	}

	// Modification detection uses total text-length inequality as a cheap
	// proxy for "this node's text changed". A `±1` character tolerance is
	// applied per node to absorb ProseMirror normalisation artefacts
	// (single trailing whitespace, newline, punctuation injection/strip
	// on block close) — same rationale as the session-wide
	// `noMeaningfulEffect` derivation. Same-length rewrites are still a
	// silent false negative — see the JSDoc on
	// `OutcomeAnalysis.unintendedRegionModified` for the known limitation.
	let unintendedRegionModified = false;
	for (const [localId, preRecord] of pre.byLocalId) {
		const postRecord = post.byLocalId.get(localId);
		if (postRecord === undefined) {
			continue;
		}
		if (
			Math.abs(preRecord.textLength - postRecord.textLength) > 1 &&
			!affectedLocalIds.has(localId)
		) {
			unintendedRegionModified = true;
			break;
		}
	}

	const totalNodeDelta = post.totalNodes - pre.totalNodes;
	const intent = inferSessionIntent(toolCalls);
	const intentEffectMismatch = detectIntentEffectMismatch(intent, totalNodeDelta);

	return {
		totalNodeDelta,
		removedNodeCount,
		textCharacterDelta: post.totalTextLength - pre.totalTextLength,
		affectedLocalIds,
		unintendedRegionModified,
		unintendedRegionRemoved,
		protectedContentRemoved: false,
		intentEffectMismatch,
	};
}
